import LegalPageShell from "./LegalPageShell";

/**
 * Rate-ED Terms & Conditions — official text provided by the platform owner.
 * If you edit this, also bump `terms_version` in /app/backend/server.py so existing
 * users can be re-prompted to re-accept.
 */
export default function Terms() {
  return (
    <LegalPageShell
      title="Rate-ED Terms & Conditions"
      effectiveDate="1st September 2026"
      testId="terms-page"
    >
      <p className="text-xs" style={{ color: "#13A69988" }}>
        Last Updated: 1st September 2026
      </p>

      <Section title="1. Introduction">
        <p>Welcome to Rate-ED.</p>
        <p className="mt-2">
          These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and use of the
          Rate-ED mobile application, website, and all related services (collectively
          referred to as the &ldquo;Platform&rdquo;).
        </p>
        <p className="mt-2">
          By creating an account, accessing, browsing, or using the Platform, you
          acknowledge that you have read, understood, and agree to be legally bound by
          these Terms, our Privacy Policy, Refund &amp; Cancellation Policy, and any other
          policies published by Rate-ED.
        </p>
        <p className="mt-2">
          If you do not agree with these Terms, please discontinue use of the Platform
          immediately.
        </p>
      </Section>

      <Section title="2. Definitions">
        <p>For the purposes of these Terms:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><b>Rate-ED</b> refers to the company owning and operating the Platform.</li>
          <li><b>Platform</b> means the Rate-ED website, mobile application, and associated services.</li>
          <li><b>Explorer</b> means a student, parent, guardian, or any individual seeking educational services through the Platform.</li>
          <li><b>Mentor</b> means an individual, trainer, coach, teacher, therapist, academy, or institution offering educational or skill-development services through the Platform.</li>
          <li><b>Booking</b> means a confirmed reservation for a class, course, workshop, consultation, or session made through the Platform.</li>
          <li><b>Session</b> refers to an individual class or appointment.</li>
          <li><b>Content</b> means any text, photographs, videos, reviews, ratings, documents, graphics, logos, software, or other material available on the Platform.</li>
        </ul>
      </Section>

      <Section title="3. Nature of the Platform">
        <p>
          Rate-ED is an online technology marketplace that connects Explorers with
          independent Mentors.
        </p>
        <p className="mt-2">Rate-ED:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Does not certify qualifications of mentors beyond any verification process disclosed on the platform;</li>
          <li>Does not employ mentors;</li>
          <li>Does not guarantee admission, examination success, employment, or skill improvement.</li>
          <li>Does not guarantee educational outcomes;</li>
          <li>Does not provide educational instruction directly;</li>
          <li>The educational relationship exists solely between the Mentor and the Explorer.</li>
        </ul>
      </Section>

      <Section title="4. Eligibility">
        <p>To use the Platform:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Users must be at least 18 years of age.</li>
          <li>Parents or legal guardians may create and manage accounts for minors.</li>
          <li>Mentors must possess the legal capacity to provide their services.</li>
          <li>Users agree to provide complete, current, and accurate information.</li>
          <li>Rate-ED reserves the right to suspend accounts containing false or misleading information.</li>
        </ul>
      </Section>

      <Section title="4. User Accounts">
        <p>Users are responsible for:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Maintaining confidentiality of login credentials;</li>
          <li>Ensuring account information remains accurate;</li>
          <li>Immediately notifying rate-ed of unauthorized account access;</li>
          <li>All activities occurring through their account.</li>
          <li>Users may not transfer or sell their accounts to another individual.</li>
        </ul>
      </Section>

      <Section title="5. Mentor Responsibilities">
        <p>Each mentor agrees to:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Provide accurate personal and professional information;</li>
          <li>Maintain updated qualifications and certifications;</li>
          <li>Conduct sessions professionally;</li>
          <li>Treat all learners with respect;</li>
          <li>Comply with applicable laws;</li>
          <li>Maintain the confidentiality of explorer information;</li>
          <li>Avoid discriminatory behavior;</li>
          <li>Avoid abusive, offensive, or inappropriate conduct;</li>
          <li>Comply with child protection requirements where applicable.</li>
          <li>Mentors are solely responsible for obtaining any licenses, registrations, insurance, tax registrations, or statutory approvals required to provide their services.</li>
        </ul>
      </Section>

      <Section title="6. Explorer Responsibilities">
        <p>Explorers agree to:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Provide accurate student information;</li>
          <li>Attend booked sessions on time;</li>
          <li>Treat mentors respectfully.</li>
          <li>Ensure minors are appropriately supervised where necessary;</li>
          <li>Use the platform only for lawful purposes.</li>
        </ul>
      </Section>

      <Section title="8. Mentor Verification">
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Rate-ed may request verification documents including but not limited to:</li>
          <li>Government-issued identification;</li>
          <li>Educational qualifications;</li>
          <li>Professional certifications;</li>
          <li>Experience certificates;</li>
          <li>Verification conducted by rate-ed is intended solely to enhance trust and does not constitute a guarantee of competence or suitability.</li>
        </ul>
      </Section>

      <Section title="9. Bookings">
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Bookings become confirmed only after successful payment and acceptance by the Mentor where applicable.</li>
          <li>Rate-ED reserves the right to cancel or modify bookings due to technical issues, payment failures, or policy violations.</li>
        </ul>
      </Section>

      <Section title="10. Payments">
        <p>
          Payments shall be processed through approved payment gateways available on the
          Platform.
        </p>
        <p className="mt-2">Rate-ED may:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Collect commissions;</li>
          <li>Deduct applicable taxes;</li>
          <li>Deduct platform fees;</li>
          <li>Process refunds in accordance with the refund policy;</li>
          <li>Temporarily withhold payouts in case of disputes or suspected fraud.</li>
          <li>Mentors are responsible for the payment of their own taxes.</li>
        </ul>
      </Section>

      <Section title="11. Cancellation & Rescheduling">
        <p>
          Explorers and Mentors must comply with the published Cancellation &amp;
          Rescheduling Policy.
        </p>
        <p className="mt-2">Repeated cancellations by Mentors may result in:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Reduced visibility;</li>
          <li>Temporary suspension;</li>
          <li>Permanent removal from the platform.</li>
          <li>Repeated no-shows by explorers may result in account restrictions.</li>
        </ul>
      </Section>

      <Section title="12. Ratings & Reviews">
        <p>Only genuine participants may submit reviews.</p>
        <p className="mt-2">Users shall not:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Post false reviews;</li>
          <li>Manipulate ratings;</li>
          <li>Post defamatory content;</li>
          <li>Post offensive language;</li>
          <li>Threaten other users.</li>
        </ul>
        <p className="mt-2">
          Rate-ED reserves the right to remove reviews violating Platform policies.
        </p>
      </Section>

      <Section title="13. Off-Platform Transactions">
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>To protect the integrity of the Platform, all bookings initiated through Rate-ED should continue to be managed through the Platform.</li>
          <li>Users shall not intentionally circumvent the Platform by arranging direct cash payments or external payment methods for services first introduced through Rate-ED.</li>
        </ul>
        <p className="mt-2">Violation may result in:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Account suspension;</li>
          <li>Account termination;</li>
          <li>Forfeiture of pending payouts;</li>
          <li>Recovery of unpaid platform fees where permitted by law.</li>
        </ul>
      </Section>

      <Section title="14. Communications">
        <p>Users consent to receiving communications from Rate-ED via:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Email;</li>
          <li>SMS;</li>
          <li>WhatsApp;</li>
          <li>Push notifications;</li>
          <li>Telephone calls (where necessary).</li>
        </ul>
        <p className="mt-2">
          These communications may include booking confirmations, reminders, support
          messages, promotional offers, policy updates, and service announcements.
        </p>
      </Section>

      <Section title="15. Child Safety">
        <p>Mentors must maintain appropriate professional boundaries at all times.</p>
        <p className="mt-2">Any allegation involving:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Child abuse;</li>
          <li>Harassment;</li>
          <li>Inappropriate conduct;</li>
          <li>Exploitation;</li>
          <li>Criminal behaviour;</li>
        </ul>
        <p className="mt-2">
          may result in immediate suspension pending investigation.
        </p>
        <p className="mt-2">
          Rate-ED reserves the right to report such matters to appropriate authorities.
        </p>
      </Section>

      <Section title="16. User Conduct">
        <p>Users shall not:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Impersonate another individual;</li>
          <li>Upload false documents;</li>
          <li>Use abusive language;</li>
          <li>Harass other users;</li>
          <li>Violate intellectual property rights;</li>
          <li>Distribute malware;</li>
          <li>Attempt unauthorized access;</li>
          <li>Interfere with platform security;</li>
          <li>Use bots or automated tools;</li>
          <li>Scrape platform data;</li>
          <li>Create fake accounts;</li>
          <li>Engage in fraudulent activities.</li>
        </ul>
      </Section>

      <Section title="17. Intellectual Property">
        <p>All Platform content including:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Software;</li>
          <li>Logos;</li>
          <li>Trademarks;</li>
          <li>Databases;</li>
          <li>Graphics;</li>
          <li>Text;</li>
          <li>Photographs;</li>
          <li>Videos;</li>
          <li>Branding;</li>
        </ul>
        <p className="mt-2">
          is the exclusive property of Rate-ED or its licensors.
        </p>
        <p className="mt-2">
          Users may not reproduce, modify, distribute, or commercially exploit Platform
          content without prior written permission.
        </p>
      </Section>

      <Section title="18. Privacy">
        <p>
          Collection and processing of personal information is governed by the Rate-ED
          Privacy Policy.
        </p>
        <p className="mt-2">
          By using the Platform, users consent to the collection and processing of
          information as described therein.
        </p>
      </Section>

      <Section title="19. Third-Party Services">
        <p>
          The Platform may integrate third-party services including payment gateways,
          communication providers, mapping services, analytics providers, and cloud
          hosting services.
        </p>
        <p className="mt-2">
          Rate-ED shall not be responsible for interruptions or failures caused by
          third-party providers.
        </p>
      </Section>

      <Section title="20. Disclaimer">
        <p>
          Rate-ED makes reasonable efforts to maintain Platform availability but does not
          warrant uninterrupted, error-free, or secure access at all times.
        </p>
        <p className="mt-2">Rate-ED does not guarantee:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Educational outcomes;</li>
          <li>Mentor availability;</li>
          <li>Uninterrupted internet connectivity;</li>
          <li>Examination results;</li>
          <li>Employment opportunities.</li>
        </ul>
      </Section>

      <Section title="21. Limitation of Liability">
        <p>To the fullest extent permitted by applicable law, Rate-ED shall not be liable for:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>indirect damages;</li>
          <li>consequential losses;</li>
          <li>loss of profits;</li>
          <li>emotional distress;</li>
          <li>educational outcomes;</li>
          <li>disputes between users;</li>
          <li>injuries during sessions;</li>
          <li>property damage caused by users.</li>
        </ul>
        <p className="mt-2">
          Where liability cannot legally be excluded, the maximum liability of Rate-ED
          shall not exceed the platform fees actually received by Rate-ED in relation to
          the relevant booking.
        </p>
      </Section>

      <Section title="22. Indemnity">
        <p>
          Users agree to indemnify and hold harmless Rate-ED, its directors, employees,
          officers, affiliates, and representatives against all claims, losses, damages,
          liabilities, costs, and expenses arising from:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Breach of these terms;</li>
          <li>Misuse of the platform.</li>
          <li>Violation of applicable laws;</li>
          <li>Infringement of third-party rights.</li>
        </ul>
      </Section>

      <Section title="23. Suspension & Termination">
        <p>
          Rate-ED may suspend or terminate any account without prior notice in cases
          involving:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Fraud;</li>
          <li>Fake documentation;</li>
          <li>Abuse;</li>
          <li>Repeated policy violations;</li>
          <li>Illegal activity;</li>
          <li>Non-payment;</li>
          <li>Platform misuse.</li>
        </ul>
      </Section>

      <Section title="24. Force Majeure">
        <p>
          Rate-ED shall not be liable for delays or failures caused by events beyond its
          reasonable control including:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>natural disasters;</li>
          <li>war;</li>
          <li>pandemics;</li>
          <li>government actions;</li>
          <li>internet outages;</li>
          <li>cyber-attacks;</li>
          <li>power failures;</li>
          <li>labour disputes.</li>
        </ul>
      </Section>

      <Section title="25. Governing Law & Jurisdiction">
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>These Terms shall be governed by and interpreted in accordance with the laws of India.</li>
          <li>Any dispute arising from these Terms shall be subject to the exclusive jurisdiction of the competent courts located in Mumbai, Maharashtra, India.</li>
        </ul>
      </Section>

      <Section title="26. Amendments">
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Rate-ED reserves the right to modify these Terms at any time.</li>
          <li>Updated Terms shall become effective upon publication on the Platform.</li>
          <li>Continued use of the Platform constitutes acceptance of the revised Terms.</li>
        </ul>
      </Section>

      <Section title="27. Contact Information">
        <p>For questions regarding these Terms, users may contact:</p>
        <p className="mt-2"><b>Rate-ED Support</b></p>
        <p>
          Email:{" "}
          <a href="mailto:support@rate-ed.in" className="font-bold underline">
            support@rate-ed.in
          </a>
        </p>
        <p>
          Website:{" "}
          <a
            href="https://www.rate-ed.in"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold underline"
          >
            www.rate-ed.in
          </a>
        </p>
      </Section>

      <Section title="28. Acceptance">
        <p>
          By clicking &ldquo;I Agree&rdquo;, creating an account, or continuing to use the Platform,
          you acknowledge that you have read, understood, and agreed to these Terms &amp;
          Conditions.
        </p>
      </Section>
    </LegalPageShell>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-base uppercase tracking-wide" style={{ color: "#13A699" }}>
        {title}
      </h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}
